import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { glassPanel } from "./glass";
import { quoteServiceDisplayPrice } from "./quotePricing";

/**
 * Horizontally scrollable service cards — tap to select/deselect for the quote.
 * @param {{ id: string, name: string, description: string, price: string, pricingModel?: string, pricePerSqFt?: number|null, image: string }[]} services
 */
export default function ServiceCarousel({
  services = [],
  selectedServiceIds = [],
  onToggleService,
  estimatedLotSqFt = null,
}) {
  const selectedSet = new Set(selectedServiceIds);

  if (!services.length) {
    return null;
  }

  return (
    <Box
      component="section"
      sx={{
        mt: 0.5,
        mb: 1,
        pb: 2,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
      aria-label="Local services"
    >
      <Typography
        variant="h6"
        fontWeight={700}
        letterSpacing="-0.02em"
        sx={{ px: 2, mb: 0.25, width: "100%", textAlign: "center" }}
      >
        Local services
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ px: 2, mb: 1.25, maxWidth: 560, textAlign: "center", mx: "auto" }}
      >
        Tap a service to include it in your request — tap again to remove. You can choose more than one.
      </Typography>

      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          display: "flex",
          justifyContent: "center",
          pb: 1.5,
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            height: 10,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0,0,0,0.04)",
            borderRadius: 5,
            mx: 2,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.18)",
            borderRadius: 5,
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.28)",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            px: 2,
            width: "max-content",
          }}
        >
        {services.map((d) => {
          const selected = selectedSet.has(d.id);
          const priceLabel = quoteServiceDisplayPrice(
            {
              price: d.price,
              pricingModel: d.pricingModel,
              pricePerSqFt: d.pricePerSqFt,
            },
            estimatedLotSqFt
          );
          return (
            <Card
              key={d.id}
              sx={{
                flex: "0 0 auto",
                width: { xs: "min(280px, 85vw)", sm: 300 },
                scrollSnapAlign: "start",
                scrollMarginLeft: 0,
                ...glassPanel,
                borderRadius: 3,
                display: "flex",
                flexDirection: "column",
                border: "2px solid",
                borderColor: selected ? "primary.main" : "rgba(255, 255, 255, 0.65)",
                boxShadow: selected
                  ? (theme) =>
                      `0 4px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px ${theme.palette.primary.main}33, inset 0 1px 0 rgba(255, 255, 255, 0.75)`
                  : glassPanel.boxShadow,
                bgcolor: selected ? "rgba(45, 106, 79, 0.08)" : undefined,
                transition: "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              <CardActionArea
                onClick={() => onToggleService?.(d.id)}
                aria-pressed={selected}
                aria-label={`${d.name}, ${priceLabel || ""}. ${selected ? "Selected" : "Not selected"}. Tap to toggle.`}
                sx={{
                  borderRadius: 2.5,
                  alignItems: "stretch",
                  flexDirection: "column",
                  display: "flex",
                }}
              >
                <Box sx={{ position: "relative" }}>
                  {d.image ? (
                    <CardMedia
                      component="img"
                      image={d.image}
                      alt=""
                      height="140"
                      sx={{ objectFit: "cover" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 140,
                        background:
                          "linear-gradient(135deg, rgba(45,106,79,0.14) 0%, rgba(76,175,80,0.1) 100%)",
                      }}
                    />
                  )}
                  {selected ? (
                    <CheckCircleIcon
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        color: "primary.main",
                        fontSize: 30,
                        filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.35))",
                      }}
                      aria-hidden
                    />
                  ) : null}
                </Box>
                <CardContent sx={{ pb: 2, pt: 1.5, flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} component="div" gutterBottom>
                    {d.name}
                  </Typography>
                  <Typography variant="body2" color="primary" fontWeight={600}>
                    {priceLabel}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.45 }}>
                    {d.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
        </Box>
      </Box>
    </Box>
  );
}
